/**
 * The Elections Tool (Website ADRs 0003 and 0005). One script, three views,
 * mounted on <div data-bsc-tool="elections" data-api-base="https://...">.
 *
 * This file is the shell: it owns the mount, history, scroll restoration and
 * view dispatch. Views own everything inside their region.
 */
import {
  announce,
  ensureLiveRegion,
  focusView,
  installLoadingView,
  setTitle,
} from "./a11y";
import { createApi } from "./api";
import { createMapboxSearch } from "./mapbox";
import {
  createMyBallotStateStore,
  opaqueHistoryState,
} from "./my-ballot-state";
import { uid } from "./dom";
import { parseUrl, type View } from "./router";
import type { ToolContext, ViewRenderer } from "./context";
import { renderDirectory } from "./views/directory";
import { renderElection } from "./views/election";
import { renderPerson } from "./views/person";
import { scrollRaceIntoView } from "./components/race-scroll-spy";

const VIEW_LABEL: Record<View["kind"], string> = { directory: "", election: "Election", person: "Person" };

const VIEWS: { [K in View["kind"]]: ViewRenderer<Extract<View, { kind: K }>> } = {
  directory: renderDirectory,
  election: renderElection,
  person: renderPerson,
};

/** Origin the script was served from; the API default when no data-api-base. */
const SCRIPT_ORIGIN = (() => {
  const src = (document.currentScript as HTMLScriptElement | null)?.src;
  try {
    return src ? new URL(src).origin : location.origin;
  } catch {
    return location.origin;
  }
})();

/**
 * iOS Safari paints scrolling content into the status bar unless a real fixed
 * element touches the top edge above everything else; the site header floats
 * below it, and a pseudo-element does not count. This band is that element.
 * The stylesheet shows and colors it.
 */
function mountStatusBand() {
  if (document.querySelector(".bsc-status-band")) return;
  const band = document.createElement("div");
  band.className = "bsc-status-band";
  band.setAttribute("aria-hidden", "true");
  document.body.prepend(band);
}

function mount(root: HTMLElement) {
  root.classList.add("bsc-elections");
  mountStatusBand();
  const apiBase = (root.dataset.apiBase || SCRIPT_ORIGIN).replace(/\/+$/, "");
  const mapboxToken = root.dataset.mapboxToken?.trim() ?? "";

  // Scroll positions per history entry. Each entry carries a key in
  // history.state so back and forward can find theirs.
  const scrolls = new Map<string, number>();
  let directoryReturn: { href: string; scroll: number } | null = null;
  let currentUrl = location.href;
  function rememberDirectory(href: string) {
    if (parseUrl(new URL(href)).kind === "directory") directoryReturn = { href, scroll: window.scrollY };
  }
  const returnFocus = new Map<string, "input" | "submit">();
  const newKey = () => Math.random().toString(36).slice(2);
  let currentKey: string = history.state?.bscKey ?? newKey();
  const myBallotState = createMyBallotStateStore(newKey);
  history.replaceState(opaqueHistoryState(history.state, currentKey), "");
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";

  let renderToken = 0;
  let viewController = new AbortController();

  const live = ensureLiveRegion(root);
  let region: HTMLElement | null = null;

  /**
   * Render the view the URL names. `focus` is false only on the first render:
   * a direct visit must not pull focus away from the page's own skip links.
   */
  async function show(opts: { focusTarget?: string; scroll: number | null; focus: boolean; restore?: boolean }) {
    viewController.abort();
    viewController = new AbortController();
    const token = ++renderToken;
    const view = parseUrl(new URL(location.href));
    const next = document.createElement("section");
    next.className = `bsc-elections__view bsc-elections__view--${view.kind}`;
    next.dataset.view = view.kind;
    root.dataset.view = view.kind;

    const render = VIEWS[view.kind] as ViewRenderer;
    // Async renderers paint their loading state before their first await.
    // Start that work off-DOM, then transfer focus before removing the old
    // focused view so focus never falls back to <body> on a slow response.
    const rendered = render(next, view, ctx);
    const previous = region;
    installLoadingView(next, previous, live, opts.focus);
    region = next;
    // A navigation that lands at the top goes there now: left at the old position, the
    // short loading view would clamp the page to its footer and send the header away.
    if (!opts.restore && opts.scroll === 0) window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });

    const name = await rendered;
    if (token !== renderToken) return; // superseded by a later navigation

    const h1 = next.querySelector("h1");
    if (h1) {
      h1.id ||= uid("view-title");
      next.setAttribute("aria-labelledby", h1.id);
    }
    if (opts.focus) {
      const preferred = next.querySelector<HTMLElement>(opts.focusTarget ?? "[data-bsc-view-focus]");
      const preferredAnnouncement = preferred?.dataset.bscViewAnnouncement;
      announce(
        preferredAnnouncement ??
          (VIEW_LABEL[view.kind] ? `${VIEW_LABEL[view.kind]}: ${name}` : name)
      );
      const finderTarget = returnFocus.get(currentKey);
      const finder = finderTarget
        ? next.querySelector<HTMLElement>(
            finderTarget === "input"
              ? ".bsc-ballot-finder__input"
              : ".bsc-ballot-finder__submit"
          )
        : null;
      if (finder) {
        returnFocus.delete(currentKey);
        finder.focus({ preventScroll: true });
      } else {
        if (preferred) preferred.focus({ preventScroll: true });
        else focusView(next);
      }
    }
    setTitle(name);
    // Back/forward restore where the visitor was. Otherwise a view may name
    // where to land (the Race a URL selects), else a navigation starts at
    // the top and a first render leaves the page alone.
    const target = next.querySelector<HTMLElement>("[data-bsc-scroll-target]");
    if (opts.restore && opts.scroll !== null) window.scrollTo({ top: opts.scroll, behavior: "instant" as ScrollBehavior });
    else if (target) {
      if (target.classList.contains("bsc-race")) scrollRaceIntoView(target);
      else target.scrollIntoView({ block: "start" });
    }
    else if (opts.scroll !== null) window.scrollTo({ top: opts.scroll, behavior: "instant" as ScrollBehavior });
  }

  const ctx: ToolContext = {
    api: createApi({ base: apiBase }),
    mapbox: mapboxToken ? createMapboxSearch({ token: mapboxToken }) : null,
    viewSignal: () => viewController.signal,
    myBallot: () => myBallotState.get(currentKey),
    enterMyBallot(snapshot) {
      const priorScroll = window.scrollY;
      scrolls.set(currentKey, priorScroll);
      const entered = myBallotState.enter(currentKey, {
        ...snapshot,
        priorScroll,
      });
      currentKey = entered.key;
      history.pushState(
        opaqueHistoryState(history.state, currentKey),
        "",
        location.href
      );
      void show({ scroll: 0, focus: true });
    },
    invalidateMyBallot() { myBallotState.leave(currentKey); },
    leaveMyBallot() {
      const left = myBallotState.leave(currentKey);
      if (!left) return;
      scrolls.set(left.originKey, left.snapshot.priorScroll);
      returnFocus.set(left.originKey, left.snapshot.finderFocusTarget);
      history.go(-left.steps);
    },
    pageUrl: () => new URL(location.href),
    directoryReturn: () => directoryReturn ? { ...directoryReturn } : null,
    navigate(href) {
      rememberDirectory(location.href);
      scrolls.set(currentKey, window.scrollY);
      const fromKey = currentKey;
      const nextKey = newKey();
      const fromView = parseUrl(new URL(location.href));
      const toView = parseUrl(new URL(href, location.href));
      const snapshot = myBallotState.get(fromKey);
      if (
        snapshot &&
        (toView.kind === "person" ||
          (fromView.kind === "person" &&
            toView.kind === "election" &&
            toView.cycle === snapshot.cycle))
      ) {
        myBallotState.copy(fromKey, nextKey);
      }
      currentKey = nextKey;
      history.pushState(opaqueHistoryState(history.state, currentKey), "", href);
      currentUrl = location.href;
      const restoredDirectory = toView.kind === "directory" && directoryReturn?.href === location.href ? directoryReturn.scroll : 0;
      void show({ scroll: restoredDirectory, focus: true });
    },
    replace(href) {
      history.replaceState(opaqueHistoryState(history.state, currentKey), "", href);
      currentUrl = location.href;
    },
    rerender(focusTarget) {
      void show({ scroll: null, focus: true, focusTarget });
    },
  };

  root.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!link || !root.contains(link)) return;
    if ((link.target && link.target !== "_self") || link.hasAttribute("download")) return;
    // Views handle their own in-view links (a Race in the navigator).
    if (link.hasAttribute("data-bsc-local")) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname) return;
    if (url.search === location.search && url.hash) return; // an in-page anchor
    event.preventDefault();
    ctx.navigate(url.href);
  });

  window.addEventListener("popstate", (event) => {
    rememberDirectory(currentUrl);
    currentUrl = location.href;
    scrolls.set(currentKey, window.scrollY);
    currentKey = event.state?.bscKey ?? newKey();
    if (!event.state?.bscKey) history.replaceState(opaqueHistoryState(event.state, currentKey), "");
    void show({ scroll: scrolls.get(currentKey) ?? 0, focus: true, restore: true });
  });

  void show({ scroll: null, focus: false });
}

function mountAll() {
  // One mount per page: the Tool owns the page's history.
  const root = document.querySelector<HTMLElement>('[data-bsc-tool="elections"]');
  if (!root || root.dataset.bscMounted === "true") return;
  root.dataset.bscMounted = "true";
  mount(root);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountAll, { once: true });
} else {
  mountAll();
}
